import { HDate, HebrewCalendar } from '@hebcal/core';
import { toWallClockDate } from '@/utils/wall_clock_date';
import { isIsraelOrganization, isYizkorDay } from '@/utils/yizkor';

export const YURTZEITEN_DISPLAY_DAYS_DEFAULT = 7;
export const YURTZEITEN_DISPLAY_DAYS_MIN = 0;
export const YURTZEITEN_DISPLAY_DAYS_MAX = 365;

/**
 * One normalizer for organization rows, half-entered form values, and the
 * values sent to the API. Old organizations already default to seven days;
 * malformed legacy values safely resolve to the same behavior.
 */
export const normalizeYurtzeitenDisplayDays = (value: unknown): number => {
  if (value === null || value === undefined || String(value).trim() === '') {
    return YURTZEITEN_DISPLAY_DAYS_DEFAULT;
  }

  const parsed = Number(value);
  if (!Number.isFinite(parsed)) return YURTZEITEN_DISPLAY_DAYS_DEFAULT;

  return Math.min(
    YURTZEITEN_DISPLAY_DAYS_MAX,
    Math.max(YURTZEITEN_DISPLAY_DAYS_MIN, Math.round(parsed))
  );
};

/**
 * Which calendar the organization counts its display window from.
 *
 * True — the default, and what an organization that has never touched the
 * setting stores — means the HEBREW anniversary, which is the day a yahrzeit is
 * actually observed on. False means the Gregorian one.
 *
 * One organization-wide choice, set in Yurtzeiten Display Settings. It used to
 * be asked of every submitter on every record, which put rows counted from
 * different calendars side by side in the same row of cards; a congregation
 * observes yahrzeits on one calendar, so the choice belongs to the congregation.
 */
export const readYurtzeitenDisplayFromHebrewDate = (
  organization: Record<string, any> | null | undefined
): boolean => organization?.yurtzeitenDisplayFromHebrewDate !== false;

/**
 * Whether every yahrzeit goes on screen on a Yizkor day, whatever its date.
 *
 * Yizkor remembers every departed at once, so on those four days the display
 * window above is set aside and the board carries the organization's whole
 * list. Off unless the organization switched it on in Yurtzeiten Display
 * Settings — an absent column is an organization that has never chosen.
 */
export const readYurtzeitenDisplayOnYizkorDays = (
  organization: Record<string, any> | null | undefined
): boolean => organization?.yurtzeitenDisplayOnYizkorDays === true;

export interface YurtzeitenDisplayWindow {
  daysBefore: number;
  daysAfter: number;
  fromHebrewDate: boolean;
  onYizkorDays: boolean;
}

export const readYurtzeitenDisplayWindow = (
  organization: Record<string, any> | null | undefined
): YurtzeitenDisplayWindow => ({
  daysBefore: normalizeYurtzeitenDisplayDays(
    organization?.yurtzeitenDisplayDaysBefore
  ),
  daysAfter: normalizeYurtzeitenDisplayDays(
    organization?.yurtzeitenDisplayDaysAfter
  ),
  fromHebrewDate: readYurtzeitenDisplayFromHebrewDate(organization),
  onYizkorDays: readYurtzeitenDisplayOnYizkorDays(organization),
});

/* ----------------------------- eligibility -------------------------------- */

const MS_PER_DAY = 24 * 60 * 60 * 1000;

/** Midnight local, so what follows compares calendar days and never clock times. */
const startOfDay = (date: Date): Date =>
  new Date(date.getFullYear(), date.getMonth(), date.getDate());

/**
 * Whole calendar days from `from` to `to` — positive while `to` is still ahead.
 *
 * Rounded rather than truncated: a day that crosses a daylight-saving boundary
 * is 23 or 25 hours long, and that hour is enough to drop a plain division onto
 * the wrong side of an integer.
 */
const daysBetween = (from: Date, to: Date): number =>
  Math.round((startOfDay(to).getTime() - startOfDay(from).getTime()) / MS_PER_DAY);

/**
 * `day` of `month` in the closest year before `hyear` that has that day in it.
 * Not every year does: only a leap year has an Adar II, and 30 Cheshvan and 30
 * Kislev exist only in years those months run long. A leap year comes round at
 * least every three years and the long months far more often, so the search
 * never runs far; null is for a day the calendar has never held.
 */
const anchorBefore = (day: number, month: number, hyear: number): HDate | null => {
  for (let year = hyear - 1; year >= hyear - 8; year -= 1) {
    if (month > HDate.monthsInYear(year)) continue;
    if (day > HDate.daysInMonth(month, year)) continue;
    return new HDate(day, month, year);
  }
  return null;
};

/**
 * The anniversary of `death` that falls in `hyear`, by hebcal's own yahrzeit
 * rule — 30 Cheshvan and 30 Kislev folded in a year those months are short, an
 * Adar II date kept in the last Adar of a plain year — or null where the Hebrew
 * calendar cannot place one.
 *
 * hebcal declines to count an anniversary in the year of the death or before
 * it. Right for a date of death; wrong for what a recurring record's Hebrew
 * date so often is: the day and month the yahrzeit is kept on, carried in
 * whatever year the picker happened to be open to. A record whose Gregorian
 * date says 2012 and whose Hebrew date reads 24 Elul 5786 — the year it was
 * entered — had, to hebcal, no yahrzeit until 5787, a year off, and dropped out
 * of a window that was open to it all year. The Gregorian path never had this
 * problem: it carries the month and day into the years around today whatever
 * year the date holds. This does the same for the Hebrew one. When hebcal
 * declines, the date is re-anchored in the nearest earlier year that has that
 * day, and hebcal is asked again from there — so its folding still applies,
 * only counted from a year that can have an anniversary in `hyear`.
 */
const hebrewAnniversary = (death: HDate, hyear: number): HDate | null => {
  try {
    const direct = HebrewCalendar.getYahrzeit(hyear, death);
    if (direct) return direct;
  } catch {
    // A date the Hebrew calendar cannot place in that year. The years either
    // side still stand on their own.
    return null;
  }

  const anchor = anchorBefore(death.getDate(), death.getMonth(), hyear);
  if (!anchor) return null;
  try {
    return HebrewCalendar.getYahrzeit(hyear, anchor) || null;
  } catch {
    return null;
  }
};

/**
 * The days near `today` this record could be shown around.
 *
 * A yahrzeit repeats every year, so the window is measured against the
 * anniversary falling near today — not against the date of passing itself,
 * which for most records is decades back and would never sit inside any window.
 * Three years are offered (last, this, next) so a window that spans the turn of
 * the year still finds the anniversary on the other side of it.
 *
 * A record marked not recurring has exactly one day: its own date.
 *
 * `fromHebrew` is the organization's calendar choice, passed in rather than read
 * off the record — every yahrzeit on a board is counted the same way.
 */
const yurtzeitenOccurrences = (
  record: Record<string, any>,
  today: Date,
  fromHebrew: boolean
): Date[] => {
  // The Hebrew date is nullable on records that predate the split, and the
  // Gregorian one is required on every record — so it is the fallback.
  const source = toWallClockDate(
    (fromHebrew && record?.hebrewDate) || record?.yahrzeitDate
  );
  if (!source) return [];

  if (record?.isRecurring === false) return [source];

  if (fromHebrew) {
    // hebcal's own yahrzeit rule, rather than a naive same-day-next-year: it
    // folds 30 Cheshvan and 30 Kislev onto the 29th in a year those months are
    // short, and an Adar II date onto Adar in a year that has only one. The one
    // thing it is not left to decide is whether the record's year gates the
    // anniversary at all — see `hebrewAnniversary`.
    let hebrewYear: number;
    let death: HDate;
    try {
      hebrewYear = new HDate(today).getFullYear();
      death = new HDate(source);
    } catch {
      return [];
    }

    return [-1, 0, 1].reduce<Date[]>((days, offset) => {
      const occurrence = hebrewAnniversary(death, hebrewYear + offset);
      if (occurrence) days.push(occurrence.greg());
      return days;
    }, []);
  }

  return [-1, 0, 1].map(
    (offset) =>
      new Date(today.getFullYear() + offset, source.getMonth(), source.getDate())
  );
};

/**
 * Whether one record falls inside the organization's display window.
 *
 * `daysBefore` reaches forward — a yahrzeit still that many days away is already
 * on screen. `daysAfter` reaches back, keeping it there once the day has passed.
 * The yahrzeit itself is always inside the window, which is what makes 0 / 0
 * mean "that exact day".
 */
export const isYurtzeitenWithinDisplayWindow = (
  record: Record<string, any> | null | undefined,
  window: Partial<YurtzeitenDisplayWindow> | null | undefined,
  today: Date = new Date()
): boolean => {
  if (!record) return false;

  const daysBefore = normalizeYurtzeitenDisplayDays(window?.daysBefore);
  const daysAfter = normalizeYurtzeitenDisplayDays(window?.daysAfter);
  // Absent means the Hebrew anniversary, the same default the organization
  // column carries — so a caller that passes only the two day counts still gets
  // the calendar every board used before the setting existed.
  const fromHebrew = window?.fromHebrewDate !== false;

  return yurtzeitenOccurrences(record, today, fromHebrew).some((occurrence) => {
    // Positive while the yahrzeit is still ahead; negative once it has passed.
    const daysUntil = daysBetween(today, occurrence);
    return daysUntil <= daysBefore && daysUntil >= -daysAfter;
  });
};

/**
 * Whether `today` is a day the organization shows every yahrzeit on: a Yizkor
 * day, with the setting switched on. Which four days those are follows the
 * calendar the organization keeps.
 */
export const isYurtzeitenYizkorDisplayDay = (
  organization: Record<string, any> | null | undefined,
  today: Date = new Date()
): boolean =>
  readYurtzeitenDisplayOnYizkorDays(organization) &&
  isYizkorDay(today, { inIsrael: isIsraelOrganization(organization) });

/**
 * The same test across a list, against the window the organization stores.
 *
 * On a Yizkor day, with the setting on, the window is not consulted at all: the
 * whole list goes on screen, in the order it arrived — a yahrzeit months away
 * and a one-off whose day has passed alike, since Yizkor remembers everyone.
 */
export const filterYurtzeitenByDisplayWindow = <T>(
  records: T[],
  organization: Record<string, any> | null | undefined,
  today: Date = new Date()
): T[] => {
  const list = Array.isArray(records) ? records : [];
  if (isYurtzeitenYizkorDisplayDay(organization, today)) return list.slice();

  const window = readYurtzeitenDisplayWindow(organization);
  return list.filter((record) =>
    isYurtzeitenWithinDisplayWindow(record as Record<string, any>, window, today)
  );
};

/* ------------------------------- direction -------------------------------- */

/**
 * Which way a Yurtzeiten_N board travels across the screen.
 *
 * Stored on the organization, like every other yahrzeit display setting, and
 * read by Display.vue in place of the destination artboard's own scroll
 * direction while a yahrzeit row is the element on screen. An organization that
 * has never chosen one stores null, and the board keeps following the artboard —
 * which is what every board did before this setting existed.
 */
export type YurtzeitenCarouselDirection = 'horizontal' | 'vertical';

export const YURTZEITEN_DIRECTION_OPTIONS: Array<{
  value: YurtzeitenCarouselDirection;
  label: string;
  hint: string;
  icon: string;
}> = [
  {
    value: 'horizontal',
    label: 'Horizontal',
    hint: 'Slides right to left.',
    icon: 'pi-arrow-left',
  },
  {
    value: 'vertical',
    label: 'Vertical',
    hint: 'Scrolls bottom to top.',
    icon: 'pi-arrow-up',
  },
];

/** Null for anything that is not one of the two directions — including the
 *  empty value an organization that has never chosen one stores. */
export const normalizeYurtzeitenCarouselDirection = (
  value: unknown
): YurtzeitenCarouselDirection | null => {
  const direction = String(value ?? '').trim().toLowerCase();
  return direction === 'horizontal' || direction === 'vertical' ? direction : null;
};
